import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEUR = (n) => Number(n || 0).toFixed(2).replace(".", ",");

export default function StundenAngebot() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customerMessage, setCustomerMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/rate-card-quotes/by-token/${token}`)
      .then(({ data }) => setQuote(data))
      .catch((err) => setError(err.response?.data?.detail || "Stunden-Angebot nicht gefunden"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    if (!window.confirm("Stunden-Angebot verbindlich annehmen? Lukas Stutz wird anschließend Kontakt aufnehmen, um den konkreten Einsatz zu besprechen.")) return;
    setSending(true);
    try {
      await axios.post(`${API}/rate-card-quotes/by-token/${token}/accept`, { message: customerMessage });
      setDone("accepted");
      toast.success("Zusage übermittelt — Sie erhalten in Kürze eine Bestätigung per E-Mail");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  const decline = async () => {
    if (!window.confirm("Stunden-Angebot ablehnen?")) return;
    setSending(true);
    try {
      await axios.post(`${API}/rate-card-quotes/by-token/${token}/decline`);
      setDone("declined");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="rate-card-not-found">
        <XCircle className="w-12 h-12 text-red-700 mx-auto mb-4" />
        <h1 className="font-serif text-3xl">Stunden-Angebot nicht verfügbar</h1>
        <p className="text-[#757067] mt-3">{error || "Der Link ist ungültig oder abgelaufen."}</p>
      </div>
    );
  }

  const statusMap = {
    sent: { icon: Clock, color: "text-blue-800", bg: "bg-blue-50", border: "border-blue-200", label: "Wartet auf Ihre Rückmeldung" },
    accepted: { icon: CheckCircle2, color: "text-emerald-800", bg: "bg-emerald-50", border: "border-emerald-200", label: "Bereits angenommen" },
    declined: { icon: XCircle, color: "text-red-800", bg: "bg-red-50", border: "border-red-200", label: "Bereits abgelehnt" },
    cancelled: { icon: XCircle, color: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200", label: "Storniert" },
  };
  const S = statusMap[quote.status] || statusMap.sent;
  const Icon = S.icon;
  const canRespond = quote.status === "sent" && !done;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="rate-card-page">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Stunden-Angebot</p>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">Angebot Nr. {quote.quote_number || "—"}</h1>
      <p className="text-[#757067] mt-3">
        für <b>{quote.betrieb}</b>
        {quote.ansprechpartner ? ` · ${quote.ansprechpartner}` : ""}
      </p>

      <div className={`mt-6 border ${S.border} ${S.bg} px-4 py-3 inline-flex items-center gap-2 text-sm ${S.color}`}>
        <Icon size={18} /> {done === "accepted" ? "✓ Vielen Dank — Ihre Zusage ist eingegangen!" : done === "declined" ? "Sie haben das Angebot abgelehnt" : S.label}
      </div>

      {quote.anrede && (
        <p className="mt-10 text-[#2C2A26] font-serif text-lg leading-relaxed">
          {quote.anrede}
        </p>
      )}
      {quote.intro_text && (
        <p className="mt-4 text-[#2C2A26] leading-relaxed whitespace-pre-line">
          {quote.intro_text}
        </p>
      )}

      <div className="mt-10 border border-[#E3DECE] p-6 md:p-8 bg-[#FAFAF7]" data-testid="rate-card-rates">
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] mb-4">Konditionen</p>
        {Number(quote.rate_flat_eur) > 0 ? (
          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <div>
                <p className="text-sm font-medium">Stundensatz</p>
                <p className="text-xs text-[#757067]">pauschal für alle Tage</p>
              </div>
              <p className="font-serif text-2xl text-[#BA6D52]">{fmtEUR(quote.rate_flat_eur)} €<span className="text-xs text-[#757067] ml-1">/ h netto</span></p>
            </div>
            <p className="text-xs text-[#757067] mt-2">zzgl. gesetzliche Mehrwertsteuer (19 %).</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-baseline border-b border-[#E3DECE] pb-3">
              <div>
                <p className="text-sm font-medium">Stundensatz Werktag</p>
                <p className="text-xs text-[#757067]">Montag bis Freitag</p>
              </div>
              <p className="font-serif text-2xl text-[#BA6D52]">{fmtEUR(quote.rate_weekday_eur)} €<span className="text-xs text-[#757067] ml-1">/ h netto</span></p>
            </div>
            <div className="flex justify-between items-baseline">
              <div>
                <p className="text-sm font-medium">Stundensatz Wochenende / Feiertag</p>
                <p className="text-xs text-[#757067]">Samstag, Sonntag &amp; gesetzliche Feiertage</p>
              </div>
              <p className="font-serif text-2xl text-[#BA6D52]">{fmtEUR(quote.rate_weekend_eur)} €<span className="text-xs text-[#757067] ml-1">/ h netto</span></p>
            </div>
            <p className="text-xs text-[#757067] mt-2">zzgl. gesetzliche Mehrwertsteuer (19 %).</p>
          </div>
        )}
      </div>

      {quote.travel_note && (
        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] mb-2">Fahrtkosten</p>
          <p className="text-[#2C2A26] leading-relaxed whitespace-pre-line">{quote.travel_note}</p>
        </div>
      )}

      {quote.closing_text && (
        <p className="mt-8 text-[#2C2A26] leading-relaxed whitespace-pre-line">
          {quote.closing_text}
        </p>
      )}

      <p className="mt-10 text-xs text-[#757067] leading-relaxed border-t border-[#E3DECE] pt-6">
        Dieses Stunden-Angebot ist unverbindlich und dient als Grundlage für ein persönliches Gespräch.
        Konkrete Einsatzdaten, Umfang und ggf. Anzahlung werden im Anschluss individuell abgestimmt.
        Es gelten die <a href="/agb" className="text-[#BA6D52] underline">Allgemeinen Geschäftsbedingungen</a>.
      </p>

      {canRespond && (
        <div className="mt-10 border-t border-[#E3DECE] pt-8">
          <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Anmerkung an Lukas (optional)</label>
          <textarea
            rows={3}
            value={customerMessage}
            onChange={(e) => setCustomerMessage(e.target.value)}
            placeholder="z. B. konkretes Datum, Anlass, Rückrufwunsch …"
            className="mt-2 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] p-3 text-sm font-light"
            data-testid="rate-card-customer-message"
          />
          <div className="mt-6 flex flex-col md:flex-row gap-3 justify-end">
            <button
              onClick={decline}
              disabled={sending}
              data-testid="rate-card-decline"
              className="border border-[#E3DECE] hover:border-red-700 hover:text-red-700 px-6 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
            >
              Ablehnen
            </button>
            <button
              onClick={accept}
              disabled={sending}
              data-testid="rate-card-accept"
              className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-8 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60 inline-flex items-center gap-2"
            >
              {sending && <Loader2 size={14} className="animate-spin" />}
              Stunden-Angebot annehmen
            </button>
          </div>
          <p className="text-xs text-[#757067] mt-4 text-center">
            Mit Klick auf &bdquo;Stunden-Angebot annehmen&ldquo; signalisieren Sie Ihre grundsätzliche Bereitschaft zur Zusammenarbeit — der konkrete Einsatz wird anschließend persönlich abgestimmt.
          </p>
        </div>
      )}

      {done === "accepted" && (
        <div className="mt-10 text-center" data-testid="rate-card-thanks">
          <CheckCircle2 className="w-16 h-16 text-emerald-700 mx-auto mb-4" />
          <h2 className="font-serif text-2xl">Vielen Dank!</h2>
          <p className="text-[#757067] mt-3 max-w-lg mx-auto">
            Ihre Zusage wurde erfasst. Lukas Stutz meldet sich in Kürze persönlich, um den konkreten Einsatz zu besprechen.
          </p>
        </div>
      )}
    </div>
  );
}
